import type { IconColor } from '@common/Icon/Icon';
import { IconID } from '@common/Icon/Icon';
import { TooltipIcon } from '@common/TooltipIcon/TooltipIcon';
import { useBreakpoints } from '@hooks/useBreakpoints';
import classNames from 'classnames';
import type { ChangeEvent, FocusEvent, ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import styles from './NumberInput.module.scss';
import { NumberInputControlButtons } from './NumberInputControlButtons/NumberInputControlButtons';
import Tippy from '@tippyjs/react';

export enum LabelPosition {
  Top,
  Left,
  Right,
}

export enum LabelSize {
  Small,
  Normal,
}

export type NumberInputErrorType = NUMBER_INPUT_ERROR_TYPE;

export enum NUMBER_INPUT_ERROR_TYPE {
  ONLY_INT = 'onlyInt',
  ONLY_UNEVEN = 'onlyUneven',
  MIN_RANGE = 'minRange',
  MAX_RANGE = 'maxRange',
  EMPTY_REQUIRED = 'emptyRequired',
  NO_ERROR = '',
}

// maximum supported precision is 5
const decimalPlacesRegex = /^(-?\d+)(?:([.,])(\d{0,5}))?/;

export interface NumberInputProps {
  label?: string;
  labelPosition?: LabelPosition;
  labelSize?: LabelSize;
  errorMessage?: string;
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  onlyInt?: boolean;
  onlyUneven?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  customWidthClass?: string;
  onChange: (newValue: number | undefined) => void;
  onBlur?: (newValue: number | undefined) => void;
  onFocus?: (value: FocusEvent) => void;
  onError?: (errorType: NumberInputErrorType) => void;
  infoButtonText?: string;
  infoTooltipContent?: ReactNode;
  infoTooltipIcon?: IconID;
  infoTooltipColor?: IconColor;
  infoTooltipSize?: number;
  interactiveTooltip?: boolean;
  mobileControlButtons?: boolean;
  preventDefault?: boolean;
  required?: boolean;
  dataTestId?: string;
  disabledTooltip?: string;
  decimalPlaces?: number;
}

export const NumberInput = ({
  label,
  labelPosition,
  labelSize,
  errorMessage,
  step,
  onlyInt,
  onlyUneven,
  placeholder,
  className,
  customWidthClass,
  onBlur,
  onFocus,
  onError,
  infoButtonText,
  infoTooltipContent,
  infoTooltipIcon,
  infoTooltipColor,
  infoTooltipSize,
  interactiveTooltip,
  disabled,
  min,
  max,
  mobileControlButtons,
  onChange,
  value,
  preventDefault,
  required,
  dataTestId,
  disabledTooltip,
  decimalPlaces,
}: NumberInputProps) => {
  const [errorType, setErrorType] = useState<NumberInputErrorType>(NUMBER_INPUT_ERROR_TYPE.NO_ERROR);
  const [userInput, setUserInput] = useState<string>(value !== undefined ? Number(value).toString() : '');
  const { md } = useBreakpoints();
  const withNumberInputControlButtons = !disabled && !md && mobileControlButtons;
  const inputId = useId();

  function processInput(inputValue: string): string {
    let processedValue = inputValue;

    if (processedValue && Number.isInteger(decimalPlaces) && decimalPlaces >= 0) {
      const matchedValue = inputValue.match(decimalPlacesRegex);
      if (matchedValue?.length > 1) {
        // get the integer value
        [, processedValue] = matchedValue;
        // if we allow decimal places, attach trimmed precision while retaining the separator
        if (decimalPlaces > 0 && matchedValue[2] && matchedValue[3]) {
          processedValue += `${matchedValue[2]}${matchedValue[3].substring(0, decimalPlaces)}`;
        }
      }
    }

    return processedValue;
  }

  function validateInput(inputValue: number) {
    let errType: NumberInputErrorType = NUMBER_INPUT_ERROR_TYPE.NO_ERROR;

    if (typeof min === 'number' && inputValue < min) {
      errType = NUMBER_INPUT_ERROR_TYPE.MIN_RANGE;
    } else if (typeof max === 'number' && inputValue > max) {
      errType = NUMBER_INPUT_ERROR_TYPE.MAX_RANGE;
    } else if (onlyInt && !Number.isInteger(inputValue)) {
      errType = NUMBER_INPUT_ERROR_TYPE.ONLY_INT;
    } else if (inputValue && onlyUneven && inputValue % 2 === 0) {
      errType = NUMBER_INPUT_ERROR_TYPE.ONLY_UNEVEN;
    }

    setErrorType(errType);

    if (onError && errType) {
      onError(errType);
    }

    return !!errType;
  }

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>, isBlur = false) => {
    const input = processInput(event.target.value);

    if (!preventDefault) {
      setUserInput(input);
    }

    if (isBlur && !onBlur) {
      return;
    }

    const eventCallback = isBlur ? onBlur : onChange;

    // Reset error state before validation
    if (errorType && onError) {
      setErrorType(NUMBER_INPUT_ERROR_TYPE.NO_ERROR);
      onError(NUMBER_INPUT_ERROR_TYPE.NO_ERROR);
    }

    // If the field is not required and nothing has been entered,
    // call the callback with undefined value
    if (['', null, undefined].includes(input)) {
      if (!required) {
        eventCallback(undefined);
      } else {
        setErrorType(NUMBER_INPUT_ERROR_TYPE.EMPTY_REQUIRED);

        if (onError) {
          onError(NUMBER_INPUT_ERROR_TYPE.EMPTY_REQUIRED);
        }
      }
      return;
    }

    const newValue = Number(input);

    // Set error state and call onError handler if input is invalid
    if (!validateInput(newValue)) {
      eventCallback(newValue);
    }
  };

  useEffect(() => {
    // reset error if the value, min or max prop is changed by a higher order component and not intentionally by the user
    if (errorType && onError) {
      setErrorType(NUMBER_INPUT_ERROR_TYPE.NO_ERROR);
      onError(NUMBER_INPUT_ERROR_TYPE.NO_ERROR);
    }

    validateInput(value);
    setUserInput(value !== undefined ? Number(value).toString() : '');
  }, [value]);

  useEffect(() => {
    // reset error if the min or max prop is changed by a higher order component
    if (errorType && onError) {
      setErrorType(NUMBER_INPUT_ERROR_TYPE.NO_ERROR);
      onError(NUMBER_INPUT_ERROR_TYPE.NO_ERROR);
    }

    validateInput(Number(userInput));
  }, [min, max]);

  let numberInputField = (
    <input
      id={inputId}
      data-testid={dataTestId}
      value={userInput}
      onChange={handleOnChange}
      onBlur={event => handleOnChange(event, true)}
      onFocus={event => onFocus?.(event)}
      className={classNames(
        customWidthClass || 'w-40',
        !withNumberInputControlButtons && 'mb-2',
        'px-6',
        'py-2',
        'appearance-none',
        'focus:outline-none',
        'border',
        'border-gray-200',
        'placeholder-gray-300',
        'disabled:bg-gray-100',
        !step && styles.input,
        disabledTooltip && disabled && styles.disabledWithTooltip,
        className
      )}
      placeholder={placeholder}
      type="number"
      min={min}
      max={max}
      step={step || 'any'}
      disabled={disabled}
    />
  );

  if (withNumberInputControlButtons) {
    numberInputField = (
      <NumberInputControlButtons onChange={onChange} value={value}>
        {numberInputField}
      </NumberInputControlButtons>
    );
  }

  const getClassForLabelSize = (newLabelSize?: LabelSize) => {
    /* istanbul ignore next */
    switch (newLabelSize) {
      case LabelSize.Normal:
        return 'text-md';
      case LabelSize.Small:
        return 'text-sm';
      case undefined:
        return 'text-md';
    }
  };

  const getNumberInputFieldWithLabelForLabelPosition = (newLabelPosition: LabelPosition) => {
    switch (newLabelPosition) {
      case LabelPosition.Top:
        return (
          <div>
            <div className="mb-2 flex flex-row">
              <label
                htmlFor={inputId}
                className={classNames(getClassForLabelSize(labelSize), 'mr-2', styles.inputLabel)}
              >
                {label}
              </label>
              {((infoButtonText && infoButtonText !== '') || infoTooltipContent) && (
                <TooltipIcon
                  id={infoTooltipIcon || IconID.INFO_CIRCLE_GRAY}
                  size={infoTooltipSize !== undefined ? infoTooltipSize : 6}
                  tooltipContent={infoTooltipContent}
                  tooltipText={infoButtonText}
                  interactive={interactiveTooltip}
                  color={infoTooltipColor}
                  primary
                  iconInline
                />
              )}
            </div>
            {numberInputField}
          </div>
        );
      case LabelPosition.Left:
        return (
          <div className="w-full flex flex-row items-center">
            <label
              htmlFor={inputId}
              className={classNames('mr-3 mb-2', getClassForLabelSize(labelSize), styles.inputLabel)}
            >
              {label}
            </label>
            <span className="ml-auto">{numberInputField}</span>
          </div>
        );
      case LabelPosition.Right:
        return (
          <div>
            {
              // display an icon and a tooltip in case disabledTooltip has been set
              // this is a feature used for the overstock sale for instance
              disabledTooltip && disabled ? (
                <Tippy
                  content={
                    disabledTooltip && (
                      <p
                        className={classNames('min-w-[150px]', 'max-w-[250px]', 'text-center')}
                        dangerouslySetInnerHTML={{ __html: disabledTooltip }}
                      />
                    )
                  }
                  theme="light-border"
                >
                  <span>{numberInputField}</span>
                </Tippy>
              ) : (
                numberInputField
              )
            }
            <label htmlFor={inputId} className={classNames('ml-4', getClassForLabelSize(labelSize), styles.inputLabel)}>
              {label}
            </label>
          </div>
        );
    }
  };

  const numberInputFieldWithLabel = getNumberInputFieldWithLabelForLabelPosition(
    labelPosition !== undefined ? labelPosition : LabelPosition.Right
  );

  return (
    <>
      <div className="flex flex-row">{label ? numberInputFieldWithLabel : numberInputField}</div>
      {errorMessage && errorType && (
        <p data-testid="number-input-error-msg" className="text-red-500 text-xs pb-8 md:pb-8 xl:pb-4">
          {errorType ? errorMessage : ''}
        </p>
      )}
    </>
  );
};
